/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from 'react-router-dom';
import CustomBreadcrumbs from '@/commons/custom-breadcrumbs';
import { PATH_DASHBOARD } from '@/routes/paths';
import { hideScrollbarX, invalidateOrders, translates } from '@/utils';
import { Container, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Iconic } from '@/commons/iconic';
import { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import SkeletonKanbanColumn from '@/commons/skeleton/SkeletonKanbanColumn';
import KanbanColumnAdd from '@/commons/kanban/column/KanbanColumnAdd';
import KanbanColumn from '@/commons/kanban/column/KanbanColumn';
import { dividerOrdersService, getOrdersByIdService } from '@/services';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { TITLE } from '@/utils/constants';
import { ModalDividerItem } from '../components/modals/dividerItem';
import { useSelectedItem } from '../hooks/useSelectedItem';
import { useFetch } from '../hooks/useFetch';
import { Button } from '@PorkyCol/button';
import { cn } from '@PorkyCol/theme';

export default function OrderDividerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, fetchId, setData } = useFetch({
    fetchFunction: getOrdersByIdService,
    getData: true,
  });

  const handleGetOrdersById = async () => {
    try {
      const resolve = await fetchId(id ?? '');
      const cards: Record<string, any> = {};
      resolve.ordersDetails.forEach((item: any) => {
        cards[item.id] = {
          id: item.id,
          idProduct: item.product.product.id,
          name: item.product.product.name,
          price: item.totalProduct,
          quantity: item.quantity,
        };
      });
      const columns = {
        id: resolve.id,
        vendor: resolve.vendor.uid,
        name: resolve.table ?? 'Table',
        cardIds: Object.keys(cards),
      };
      const board = {
        table: resolve.table ?? 'Table',
        id: resolve.id,
        status: resolve.status,
        columnOrder: [resolve.id],
        columns: {
          [resolve.id]: columns,
        },
        cards,
      };
      setData(board);
    } catch (error) {
      console.log('🚀 ~ file: divider.tsx:63 ~ handleGetOrdersById ~ error:', error);
    }
  };

  const { isLoading, fetchProperties } = useFetch({ fetchFunction: dividerOrdersService });

  const handleDividerOrder = async (values: any) => {
    try {
      await fetchProperties(id!, values);
      navigate(PATH_DASHBOARD.orders.view(id));
    } catch (error) {
      console.log('🚀 ~ file: divider.tsx:76 ~ handleDividerOrder ~ error:', error);
    }
  };

  useEffect(() => {
    handleGetOrdersById();
  }, []);

  useEffect(() => {
    if (data?.columnOrder?.length) {
      setBoard(data);
    }
  }, [data]);

  const [board, setBoard] = useState<any>(data ?? { columnOrder: 0 });
  const { changeItemSelect, selectedItems, handleChange, open, onDivideItems, onReload } =
    useSelectedItem(board, setBoard);
  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    if (!destination) return;

    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    if (type === 'column') {
      const newColumnOrder = Array.from(board.columnOrder);

      newColumnOrder.splice(source.index, 1);

      newColumnOrder.splice(destination.index, 0, draggableId);

      // dispatch(persistColumn(newColumnOrder));
      setBoard((prev: any) => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));
      return;
    }

    const start = board.columns[source.droppableId];
    const finish = board.columns[destination.droppableId];

    if (start.id === finish.id) {
      const updatedCardIds = [...start.cardIds];

      updatedCardIds.splice(source.index, 1);

      updatedCardIds.splice(destination.index, 0, draggableId);

      const updatedColumn = {
        ...start,
        cardIds: updatedCardIds,
      };
      setBoard((prev: any) => ({
        ...prev,
        columns: {
          ...board.columns,
          [updatedColumn.id]: updatedColumn,
        },
      }));
      return;
    }

    const startCardIds = [...start.cardIds];

    startCardIds.splice(source.index, 1);

    const updatedStart = {
      ...start,
      cardIds: startCardIds,
    };

    const finishCardIds = [...finish.cardIds];

    finishCardIds.splice(destination.index, 0, draggableId);

    const updatedFinish = {
      ...finish,
      cardIds: finishCardIds,
    };

    setBoard((prev: any) => ({
      ...prev,
      columns: {
        ...board.columns,
        [updatedStart.id]: updatedStart,
        [updatedFinish.id]: updatedFinish,
      },
    }));
  };
  const orderParams = useCallback(
    () => ({
      fullId: data?.id ?? '',
      id: data?.id?.slice(data.id.length / 1.4) ?? '',
      table: data?.table ?? '',
      status: data?.status ?? 'created',
    }),
    [data]
  );

  const handleSubmit = useCallback(async () => {
    try {
      const newOrders = Object.values(board.columns).map((item: any) => {
        if (item.cardIds.length === 0) {
          throw new Error('Cuenta sin items');
        }

        const newOrder: Record<string, any> = {
          table: item.name,
          vendor: item.vendor,
          ordersDetails: item.cardIds.map((cardId: any) => ({
            idProduct: board.cards[cardId].idProduct,
            quantity: board.cards[cardId].quantity,
            id: cardId,
          })),
        };
        if (item.id === id) newOrder.id = item.id;
        return {
          ...newOrder,
        };
      });
      await handleDividerOrder(newOrders);
    } catch (error: any) {
      enqueueSnackbar(error?.message, { variant: 'error' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.cards, board.columns, enqueueSnackbar, id]);
  const params = orderParams() || { id: '', fullId: '' };
  return (
    <>
      <Helmet>
        <title>
          {' '}
          {TITLE}: Order Divider {params.id!}
        </title>
      </Helmet>

      <Container maxWidth={false} sx={{ pb: 2 }}>
        <CustomBreadcrumbs
          heading={`${translates.order} ${params.id!}`}
          links={[
            { name: translates.dashboard, href: PATH_DASHBOARD.root },
            {
              name: translates.order,
              href: PATH_DASHBOARD.orders.root,
            },
            { name: data?.table ?? 'No table' },
          ]}
          action={[
            <Button
              className={cn({
                '!bg-gray-700 border-gray-600 hover:border-gray-600': !(
                  board?.columnOrder?.length > 1
                ),
              })}
              key="edit-order"
              onClick={handleSubmit}
              icon={<Iconic icon="eva:edit-outline" />}
              disabled={!(board?.columnOrder?.length > 1) ?? true}
              type="submit"
              variant="solid"
              size="lg"
              color="success"
              loading={isLoading}
            >
              {translates.dividerCheckout}
            </Button>,
          ]}
        />
        {!(board?.columnOrder?.length > 0) ? (
          <SkeletonKanbanColumn />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="all-columns" direction="horizontal" type="column">
              {(provided) => (
                <Stack
                  spacing={3}
                  direction="row"
                  alignItems="flex-start"
                  sx={{
                    height: 1,
                    overflowY: 'hidden',
                    ...hideScrollbarX,
                  }}
                >
                  {board.columnOrder?.map?.((columnId: any, index: any) => (
                    <KanbanColumn
                      onClickTaskCard={changeItemSelect}
                      orderId={board.id}
                      index={index}
                      key={columnId}
                      column={board.columns[columnId]}
                      cards={board.cards}
                      setBoard={setBoard}
                      onReload={onReload}
                    />
                  ))}
                  {provided.placeholder}
                  <KanbanColumnAdd setBoard={setBoard} />
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        )}

        <ModalDividerItem
          handleClose={handleChange}
          open={open}
          handleDividerItem={onDivideItems}
          product={selectedItems.name}
          quantityP={selectedItems.quantity}
        />
      </Container>
    </>
  );
}
